// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
    Spinner,
    SpinnerSize,
    PrimaryButton,
} from 'office-ui-fabric-react';
import { IChoiceGroupOption } from 'office-ui-fabric-react/lib/ChoiceGroup';
import { IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import AddressChoices from './AddressChoices';
import AddressFields from './AddressFields';
import { countryListSelector, statusSelector } from './selector';

import ErrorMessage from './../../../components/ErrorMessage/ErrorMessage';
import { TranslationDetails, translateMessage } from './../../../helpers/errorMessageInterpreter';
import {
    validatePostalCode,
    validateProvince,
    validationByPattern,
    validationPatterns,
} from './../../../helpers/validation';

import { isWalkInCenterSelectedForMoreOffers } from './../../../helpers/shippingChecks';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { selectedCountryInfoSelector } from './../../../store/countries/selectors';
import { LoadingState } from './../../../store/actionStatus';
import { getAddressValidationData, GET_ADDRESS_VALIDATION_DATA, validatedAddressSelector, wicSelectedAddressSelector } from './../../../store/address';
import {
    getShippingAddressData, shippingAddressSelector, selectedShippingOfferSelector,
    isOnSiteRepairStatusSelector, saveCustomerServiceLocationAddress, saveCustomerComments,
    customerServiceLocationAddressSelector, secondSRCreationSelector, isSoftwareOrderSelector, zipCodeSelector,
} from './../../../store/orderCreation';
import { provincesSelector } from './../../../store/provinces';
import Styles from './address.module.scss';
import { GET_ONSITE_SOLD_TO_SHIP_TO } from './../../../store/managePartners';
import { userAgentTierSelector } from './../../../store/user';
import isVatIDRequired from 'src/lib/validateVatID';

export interface ProvinceType {
    ISOCode: string | number;
    ISOName: string;
}

export interface AddressFieldFlags {
    displayCity: string;
    displayState: string;
    displayZipcode: string;
    optionalZipcode: string;
}

const INITIAL_ADDRESS_FIELD_FLAGS = {
    displayCity: '',
    displayState: '',
    displayZipcode: '',
    optionalZipcode: '',
};

interface AddressFieldType {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    language: string;
    address1: string;
    address2: string;
    city: string;
    postalCode: string;
    country: string;
    stateOrProvince: string;
    vatId: string;
    customerPO: string;
    validation: ValidationTypes;
    secondaryContactName: string;
    secondaryContactPhoneNumber: string;
    countryShortCode?: string;
}

interface ValidationTypes {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
}
interface PropTypes {
    isBillingRequired: boolean;
    defaultToShipToAddress: boolean;
    defaultToWicShipToAddress: boolean;
    enableContinue: boolean;
    onContinueClick?: () => void;
    readonly?: boolean;
    isEnterpriseCustomer: boolean;
    isOrderSummaryEditing?: boolean;
    customerComments?: string;
}

const INITIAL_STATE = {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    language: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    country: '',
    stateOrProvince: '',
    vatId: '',
    customerPO: '',
    validation: {
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        address1: '',
        city: '',
        country: '',
    },
    countryShortCode: ''
};

export default function Address(props: PropTypes): JSX.Element {

    const dispatch = useDispatch();
    const validatedAddress = useSelector(state => validatedAddressSelector(state));
    const shippingAddress = useSelector(state => shippingAddressSelector(state));
    const customerServiceLocationAddress = useSelector(state => customerServiceLocationAddressSelector(state));
    const wicSelectedAddress = useSelector(state => wicSelectedAddressSelector(state));
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    const isSoftwareOrder = useSelector(state => isSoftwareOrderSelector(state));
    const postalCode = useSelector(state => zipCodeSelector(state));

    const [isSubmitting, setSubmitting] = useState(false);
    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const country = countryInfo && countryInfo.countryCode;
    const countryShortCode = countryInfo && countryInfo.countryShortCode;
    //keep track of the input field changes
    const [addressInputFromFields, setAddressInputFromFields] = useState<AddressFieldType>(
        props.defaultToShipToAddress ? { ...INITIAL_STATE, ...shippingAddress } : { ...INITIAL_STATE, country, postalCode, countryShortCode });

    //to keep track of the address choice of a user from the popup
    const [selectedAddressIndex, setSelectedAddressIndex] = useState('');
    const addressValidationLoadingState = useSelector(state => statusSelector(state)(GET_ADDRESS_VALIDATION_DATA));
    const onSiteSoldToState = useSelector(state => statusSelector(state)(GET_ONSITE_SOLD_TO_SHIP_TO));
    const [addressFieldFlags, setAddressFieldFlags] = useState<AddressFieldFlags>({ ...INITIAL_ADDRESS_FIELD_FLAGS });
    useEffect(() => {
        if (props.defaultToWicShipToAddress && wicSelectedAddress) {
            setAddressInputFromFields({ ...INITIAL_STATE, ...wicSelectedAddress });
        }
    },
        [props.defaultToWicShipToAddress]);

    // for showing and hiding the address choice popup
    const [hideModal, setHideModal] = useState(true);

    let addressChosen = addressInputFromFields;

    const addressFill = shippingAddress && Object.keys(shippingAddress).length !== 0 ?
        shippingAddress : customerServiceLocationAddress && Object.keys(customerServiceLocationAddress).length !== 0 ?
            customerServiceLocationAddress : {};

    //shipping/customerServiceLocationAddress is not getting filled from back button of order summary
    if (Object.keys(addressFill).length !== 0 && props.readonly && !props.isOrderSummaryEditing) {
        addressChosen.firstName = addressFill.firstName;
        addressChosen.lastName = addressFill.lastName;
        addressChosen.phoneNumber = addressFill.phoneNumber;
        addressChosen.language = addressFill.language;
        addressChosen.postalCode = addressFill.postalCode;
        addressChosen.address1 = addressFill.address1;
        addressChosen.address2 = addressFill.address2;
        addressChosen.email = addressFill.email;
        addressChosen.city = addressFill.city;
        addressChosen.country = addressFill.country;
        addressChosen.stateOrProvince = addressFill.stateOrProvince;
        addressChosen.vatId = addressFill.vatId;
        addressChosen.customerPO = addressFill.customerPO;
        addressChosen.secondaryContactName = addressFill.secondaryContactName;
        addressChosen.secondaryContactPhoneNumber = addressFill.secondaryContactPhoneNumber;
    }

    useEffect(
        () => {
            if (addressValidationLoadingState.status === LoadingState.success && isSubmitting) {
                setHideModal(false);
            }
        },
        [addressValidationLoadingState.status]);

    const { t } = useTranslation(['translation', 'lists']);

    //flag to disable input fields while submitting
    const isDisabled = isSubmitting && addressValidationLoadingState.status === LoadingState.started;

    const isError = addressValidationLoadingState.status === LoadingState.rejected ||
        (onSiteSoldToState.status === LoadingState.rejected && isOnSiteRepair);
    const addressValidationMessage = isError
        ? addressValidationLoadingState.error
        && ((addressValidationLoadingState.error.data && addressValidationLoadingState.error.data.message)
            || addressValidationLoadingState.error.message)
        : undefined;

    const onSiteSoldToErrorMessage = isError
        ? onSiteSoldToState.error
        && ((onSiteSoldToState.error.data && onSiteSoldToState.error.data.message)
            || onSiteSoldToState.error.message)
        : undefined;


    let translatedErrorMessage = '';
    if (isError && addressValidationMessage) {
        const matchedError: TranslationDetails = translateMessage(addressValidationMessage, 'address_validation_failed');
        translatedErrorMessage = t(matchedError.key, matchedError.values);
    }
    else if (isError && onSiteSoldToErrorMessage) {
        translatedErrorMessage = onSiteSoldToErrorMessage;
    }

    //Get data from the address input fields
    function onAddressInputFieldsChange(addressInput: any) {
        setAddressInputFromFields(addressInput);
    }

    const userAgentTier = useSelector(state => userAgentTierSelector(state));

    //validates for required fields
    function isValid() {
        const errors = {
            address1: validatedAddresses(addressInputFromFields.address1.trim(), t('address_1')),
            address2: validatedAddresses(addressInputFromFields.address2.trim(), t('address_2')),
            firstName: validateNames(addressInputFromFields.firstName.trim(), props.isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')),
            lastName: validateNames(addressInputFromFields.lastName.trim(), props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')),
            email: validateEmail(addressInputFromFields.email.trim()),
            country: addressInputFromFields.country ? '' : t('required_message', { fieldName: t('country') }),
            city: validateCity(addressInputFromFields.city.trim()),
            phoneNumber: validatePhoneNumber(addressInputFromFields.phoneNumber.trim()),
            postalCode: validateZipCode(addressInputFromFields.postalCode.trim()),
            stateOrProvince: validateStateOrProvince(addressInputFromFields.stateOrProvince),
            secondaryContactPhoneNumber: validateSecondaryContactPhoneNumber(addressInputFromFields.secondaryContactPhoneNumber),
            vatId: validateVatId(addressInputFromFields.vatId.trim()),
        };
        setAddressInputFromFields({ ...addressInputFromFields, validation: errors });
        return !Object.keys(errors).some(key => errors[key]);
    }

    function validateVatId(value: string) {
        if (!isVatIDRequired(userAgentTier, addressInputFromFields.country)) {
            return ''; 
        }
        
        if (!value) {
            return t('required_message', { fieldName: t('vat_id') });
        }

        return '';
    }

    function validateNames(value?: any, fieldName?: string) {
        if (!value) {
            return t('required_message', { fieldName: fieldName });
        }
        else {
            if (value?.includes("|") || value?.includes("*") || value?.includes("~")) {
                return t('invalid_character_information', { fieldName: fieldName });
            }
        }
        return '';
    }

    function validatedAddresses(value?: any, fieldName?: string) {
        if (fieldName === t('address_1')) {
            if (!value) {
                return t('required_message', { fieldName: fieldName });
            }
        }
        if (value && (value?.includes("|") || value?.includes("*") || value?.includes("~"))) {
            return t('invalid_character_information', { fieldName: fieldName });
        }
        return '';
    }

    function validateCity(value?: any) {
        if (addressFieldFlags.displayCity === 'True' && value === '' && !value) {
            return t('required_message', { fieldName: t('city') });
        }
        if (addressFieldFlags.displayCity === 'True' && value && (value?.includes("|") || value?.includes("*") || value?.includes("~"))) {
            return t('invalid_character_information', { fieldName: t('city') });
        }
        return '';
    }

    function validateStateOrProvince(province?: string) {
        if (addressFieldFlags.displayState === 'True' && !validateProvince(addressInputFromFields.country, province)) {
            return t('required_message', { fieldName: t('state_province') });
        }
        return '';
    }

    function validateZipCode(value?: any) {
        if (addressFieldFlags.displayZipcode === 'True') {
            if (addressFieldFlags.optionalZipcode === 'False' && (!value || value === '')) {
                return t('required_message', { fieldName: t('zip_code') });
            }
            if (addressFieldFlags.optionalZipcode === 'False' && !validatePostalCode(addressInputFromFields.country, value)) {
                return t('invalid_postal_code');
            }
            if (addressFieldFlags.optionalZipcode === 'True' && value && value?.length > 10) {
                return t('invalid_postal_code_characters_length');
            }
        }
        return '';
    }

    function validateEmail(value: any) {
        const { email } = validationPatterns;

        if (!value) {
            return t('required_message', { fieldName: t('email') });
        }
        if (!validationByPattern(email, value)) {
            return t('invalid_email_address');
        }
        if (value && (value?.includes("|") || value?.includes("*") || value?.includes("~"))) {
            return t('invalid_character_information', { fieldName: t('email') });
        }
        return '';
    }

    function validatePhoneNumber(value?: string) {
        const { phoneNumber } = validationPatterns;
        if (!value) {
            return t('required_message', { fieldName: t('phone_number') });
        }

        if (!validationByPattern(phoneNumber, value)) {
            return t('invalid_phone_number');
        }

        return '';
    }

    function validateSecondaryContactPhoneNumber(value?: string) {
        const { phoneNumber } = validationPatterns;

        if (!validationByPattern(phoneNumber, value)) {
            return t('invalid_phone_number');
        }

        return '';
    }

    //validate input => dispatch address validation action
    function handleContinue() {
        if (!isValid()) {
            return;
        }

        logPageAction('Address submitted: Continue button clicked');

        setSubmitting(true);
        if (addressInputFromFields) {
            const addressChosen = addressInputFromFields;
            Object.keys(addressChosen).map(inputVal => addressChosen[inputVal] = typeof addressChosen[inputVal] === 'string' ? addressChosen[inputVal].trim() : addressChosen[inputVal]);
            setAddressInputFromFields(addressChosen);
            dispatch(getAddressValidationData(addressInputFromFields));
        }
        if (props.onContinueClick) {
            props.onContinueClick();
        }

        if (isOnSiteRepair) {
            dispatch(saveCustomerComments({ customerComments: props.customerComments }));
        }
    }

    function onAddressChoiceChange(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption): void {
        if (option) {
            setSelectedAddressIndex(option.key);
        }
    }

    function onCancelAddressChoicePopupClicked() {
        setHideModal(true);
        if (props.onContinueClick) {
            props.onContinueClick();
        }
        setSubmitting(false);
    }

    //Upon popup save clicked,saves the selected address to redux state as shippingAddress and displays back to the user on the input field form
    function onSaveAddressChoicePopupClicked() {
        const indexChosen: number = parseInt(selectedAddressIndex, 10);

        if (indexChosen > -1 && addressValidationLoadingState.status === LoadingState.success) {
            const { addressMatches } = validatedAddress;
            if (addressMatches) {

                const validatedAddress = addressMatches[indexChosen];
                addressChosen = {
                    ...addressChosen,
                    address1: validatedAddress.address1,
                    address2: validatedAddress.address2,
                    city: validatedAddress.city,
                    postalCode: validatedAddress.postalCode,
                    stateOrProvince: validatedAddress.stateOrProvince,
                    country: validatedAddress.country,
                    vatId: validatedAddress.vatId ? validatedAddress.vatId : addressInputFromFields.vatId,
                    customerPO: validatedAddress.customerPO ? validatedAddress.customerPO : addressInputFromFields.customerPO,
                };
            }
        }


        if (isOnSiteRepair) {
            //put the selected customer service location address to redux store as customerServiceLocationAddress to be used in the next action
            dispatch(saveCustomerServiceLocationAddress({ ...addressChosen, validation: undefined }));
        }
        else {
            //put the selected shipping address to redux store as shippingAddress to be used in the next action
            const { secondaryContactName, secondaryContactPhoneNumber, ...shippingAddressChosen } = addressChosen;
            dispatch(getShippingAddressData({ ...shippingAddressChosen, validation: undefined }));
        }

        setSubmitting(false);
        setHideModal(true);
    }

    //prepare lists of address to show on the popup for the user to choose after validation
    const getAddressChoices = () => {
        let choices: any[] = [];
        if (addressValidationLoadingState.status === LoadingState.success) {
            const { addressMatches } = validatedAddress;
            if (addressMatches) {
                choices = addressMatches.map((address: any, index: any) => (
                    {
                        key: index,
                        text: `${address.address1} ${address.address2 || ''}, ${address.city} ${address.postalCode || ''} ${address.stateOrProvince || ''} ${address.country} `,
                    } as IChoiceGroupOption
                ));
            }
        }
        choices.push({ key: -1, text: t('use_address_provided') });
        return choices;
    };

    const countryOptions = useSelector((state: any) => countryListSelector(state));
    const selectedCountryISOShortCode = React.useMemo(() => {
        const countrySelected = countryOptions.find(
            (country: any) =>
                country.ISOCode === addressInputFromFields.country
        );
        setAddressFieldFlags(countrySelected);
        return countrySelected && countrySelected.ISOCountryShortCode;
    }, [addressInputFromFields.country]);
    
    const provinceOptions = useSelector((state: any) => provincesSelector(state)((selectedCountryISOShortCode || '').toLocaleLowerCase()));
    const languageOptions = t('lists:languages', { returnObjects: true }) as any[];
    const selectedShippingOffer = useSelector(state => selectedShippingOfferSelector(state));
    const walkInCenterOfferSelected = isWalkInCenterSelectedForMoreOffers(selectedShippingOffer);

    return (
        <section className={Styles.addressContainer}>
            <div className="ms-Grid">
                <div className={clsx(Styles.headerContainer, 'ms-Grid-row')}>
                    <div className={clsx('ms-Grid-col', 'ms-sm12')}>
                        <h3 className={Styles.h3Heading}>{isOnSiteRepair ?
                            t('service_location_address') : secondSRCreation ?
                                t('partner_shipping_address') : isSoftwareOrder ?
                                    t('customer_details') : t('shipping_address')}</h3>
                    </div>
                </div>
                <AddressFields
                    isCountryEnable={true}
                    readonly={props.readonly}
                    isWalkInCenter={walkInCenterOfferSelected}
                    disabled={isDisabled}
                    {...addressChosen}
                    onAddressInputChanges={onAddressInputFieldsChange}
                    isEnterpriseCustomer={props.isEnterpriseCustomer}
                    isOnSiteRepair={isOnSiteRepair}
                    languageOptions={
                        languageOptions
                            .map((lan: { code: string, lcid: string, name: string }) => ({ key: lan.code.toLowerCase(), text: lan.name } as IDropdownOption))}
                    countryOptions={
                        countryOptions
                            .sort()
                            .map((option: { ISOCode: string | number; ISOName: string; }) => ({ key: option.ISOCode, text: option.ISOName } as IDropdownOption))}
                    provincesOptions={
                        (provinceOptions || [] as ProvinceType[])
                            .sort()
                            .map((option: { ISOCode: string | number; ISOName: string; }) => ({ key: option.ISOCode, text: option.ISOName } as IDropdownOption))}
                    secondSRCreation={secondSRCreation}
                    zipCode={postalCode}
                    toDisplayIcon={true}
                />
                <AddressChoices
                    hide={hideModal}
                    onChoiceChange={onAddressChoiceChange}
                    options={getAddressChoices()}
                    isBillingRequired={props.isBillingRequired}
                    onCancelChoiceClicked={onCancelAddressChoicePopupClicked}
                    onSaveChoiceClicked={onSaveAddressChoicePopupClicked}
                    secondSRCreation={secondSRCreation}
                />
            </div>

            <div className={Styles.buttonContainer}>
                {(!props.readonly || isSubmitting)
                    ? (
                        <PrimaryButton
                            className={clsx(Styles.buttonPrimary, Styles.buttonAlign, Styles.continueButton)}
                            disabled={!props.enableContinue || isError}
                            onClick={handleContinue}
                            ariaLabel={t('continue')}
                            text={t('continue')}
                        />
                    ) : null
                }
                {isDisabled && !isError
                    ? (
                        <Spinner
                            className={Styles.spinnerStyle}
                            labelPosition="bottom"
                            size={SpinnerSize.medium}
                        />
                    ) : null
                }
            </div>

            <ErrorMessage message={translatedErrorMessage} somethingWentWrong={isError} traceData={addressValidationLoadingState} />
        </section>
    );
}
